import React, { useState } from "react";
import type {
  NormalizedProduct,
  SearchResults,
  ShoppingListItem,
  SourceId,
} from "../../types";
import { ALL_SOURCES, SOURCE_LABELS } from "../../types";
import { SourceBadge } from "./SourceBadge";
import { rankUnitPrice } from "../../unit-price";
import { UnitPriceBadge } from "./UnitPriceBadge";

interface Props {
  item: Partial<ShoppingListItem>;
  results: SearchResults;
  onConfirm: (
    matches: Partial<Record<SourceId, NormalizedProduct | null>>
  ) => void;
  onBack: () => void;
}

function formatPrice(price: number): string {
  return `R$ ${price.toFixed(2)}`;
}

export function MatchConfirm({ item, results, onConfirm, onBack }: Props) {
  const primary = item.primary!;
  const [matches, setMatches] = useState<
    Partial<Record<SourceId, NormalizedProduct | null>>
  >(() => {
    const m: Partial<Record<SourceId, NormalizedProduct | null>> = {
      ...item.matches,
    };
    m[primary.source] = primary;
    return m;
  });
  const [expandedSource, setExpandedSource] = useState<SourceId | null>(null);

  function setMatch(source: SourceId, product: NormalizedProduct | null) {
    setMatches((prev) => ({ ...prev, [source]: product }));
    setExpandedSource(null);
  }

  const otherSources = ALL_SOURCES.filter((s) => s !== primary.source);

  const allSourcesForItem = [primary.source, ...otherSources];
  const unitPrices = allSourcesForItem.map((s) => {
    const p = s === primary.source ? primary : matches[s];
    return p?.unitPrice ?? null;
  });
  const ranks = rankUnitPrice(unitPrices);
  const rankMap = Object.fromEntries(
    allSourcesForItem.map((s, i) => [s, ranks[i]])
  ) as Record<SourceId, ReturnType<typeof rankUnitPrice>[number]>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Confirmar equivalentes
        </h2>
        <button
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Voltar
        </button>
      </div>

      {/* Primary product */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
        <div className="flex items-center gap-2 mb-1">
          <SourceBadge source={primary.source} />
          <span className="text-xs text-gray-500 dark:text-gray-400">Produto principal</span>
        </div>
        <p className="font-medium text-gray-900 dark:text-gray-100">{primary.name}</p>
        <p className="text-sm font-bold text-green-700 dark:text-green-400">
          {formatPrice(primary.price)}
          {primary.unitPrice && (
            <span className="ml-2">
              <UnitPriceBadge
                value={primary.unitPrice.value}
                unit={primary.unitPrice.unit}
                rank={rankMap[primary.source]}
              />
            </span>
          )}
        </p>
      </div>

      {/* Matches per source */}
      {otherSources.map((source) => {
        const sr = results.results[source];
        const match = matches[source];
        const hasProducts = sr.products.length > 0;

        return (
          <div
            key={source}
            className="mb-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <SourceBadge source={source} />
              {sr.error && (
                <span className="text-xs text-red-500">{sr.error}</span>
              )}
            </div>

            {match ? (
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{match.name}</p>
                  <p className="text-sm font-bold text-green-700 dark:text-green-400">
                    {formatPrice(match.price)}
                    {match.unitPrice && (
                      <span className="ml-2">
                        <UnitPriceBadge
                          value={match.unitPrice.value}
                          unit={match.unitPrice.unit}
                          rank={rankMap[source]}
                        />
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  {hasProducts && (
                    <button
                      onClick={() =>
                        setExpandedSource(
                          expandedSource === source ? null : source
                        )
                      }
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Trocar
                    </button>
                  )}
                  <button
                    onClick={() => setMatch(source, null)}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-sm text-gray-400 dark:text-gray-500">Nao encontrado</p>
                {hasProducts && (
                  <button
                    onClick={() =>
                      setExpandedSource(
                        expandedSource === source ? null : source
                      )
                    }
                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Escolher
                  </button>
                )}
              </div>
            )}

            {/* Expanded product list */}
            {expandedSource === source && (
              <div className="mt-2 border-t dark:border-gray-700 pt-2 space-y-1 max-h-48 overflow-y-auto">
                {sr.products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setMatch(source, p)}
                    className="w-full flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-left text-sm"
                  >
                    <span className="truncate flex-1 text-gray-900 dark:text-gray-100">{p.name}</span>
                    <span className="ml-2 flex items-center gap-2 flex-shrink-0">
                      {p.unitPrice && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          R$ {p.unitPrice.value.toFixed(2)}/{p.unitPrice.unit}
                        </span>
                      )}
                      <span className="font-medium text-green-700 dark:text-green-400">
                        {formatPrice(p.price)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={() => onConfirm(matches)}
        className="w-full mt-4 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 font-medium"
      >
        Adicionar a lista
      </button>
    </div>
  );
}
